import { useEffect, useRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { DonutChart } from '../../components/common/DonutChart';
import { NoConsumptionData } from '../../components/common/NoConsumptionData';
import { useEnergyConsumption } from '../../hooks/useEnergyConsumption';
import { useConsumption } from '../../contexts/ConsumptionContext';
import { useAuth } from '../../contexts/AuthContext';

/**
 * EnergyHomePage - Página principal de consumo de energia
 * 
 * Busca dados reais da API usando o hook useEnergyConsumption
 * Endpoint: GET /consumo/energia/:id/api_data
 */
export function EnergyHomePage() {
  const { id } = useParams<{ id: string }>();
  useAuth();
  const { currentResidenceId, setCurrentResidenceId, setBeginningOfPeriod } = useConsumption();
  
  const residenceId = id || currentResidenceId;
  
  useEffect(() => {
    if (id && id !== currentResidenceId) {
      setCurrentResidenceId(id);
    }
  }, [id, currentResidenceId, setCurrentResidenceId]);

  const { data, isLoading, error, noData, refetch } = useEnergyConsumption(residenceId);

  useEffect(() => {
    if (data?.beginningOfPeriod) {
      setBeginningOfPeriod(data.beginningOfPeriod);
    }
  }, [data?.beginningOfPeriod, setBeginningOfPeriod]);

  // Dados padrão para renderização imediata (sem estado de loading)
  const defaultData = {
    usageToday: 0,
    currentMonthUsageAvg: 0,
    currentUsage: 0,
    monthlyConsumptionAvg: 0,
    forecast: 0,
    goalStatus: 'Dentro' as const,
    hasGoal: false,
    goalValue: 0,
    nextStatement: '',
    beginningOfPeriod: '',
    monthlyLabels: [],
    monthlyConsumption: [],
    dailyLabels: [],
    energyDaily: [],
    meterTypes: ['ENERGIA'],
  };

  // Não renderiza nada enquanto carrega - preloader global está visível
  if (isLoading) {
    return null;
  }

  // Mostra erro apenas se houver erro
  if (error) {
    return <EnergyHomePageError error={error} onRetry={refetch} />;
  }

  // Unidade sem medidor de energia cadastrado — estado normal, não um erro
  if (noData) {
    return <NoConsumptionData meterType="energy" />;
  }

  // Renderiza a página com dados reais ou padrão
  return (
    <EnergyHomePageContent {...(data || defaultData)} />
  );
}

function EnergyHomePageError({ error, onRetry }: { error: string | null; onRetry: () => void }) {
  const { t } = useTranslation();
  
  return (
    <div className="w-full">
      <div className="
        relative
        pl-[60px] pr-8 py-[12px]
        !bg-[#e84b3d] text-white
        rounded
        after:content-[''] after:absolute after:w-[57px] after:h-[52px]
        after:bg-[url('/images/icons-set.png')] after:bg-no-repeat
        after:top-[14px] after:left-[5px]
        after:bg-[position:11px_-226px]
      " role="alert">
        <strong className="block font-bold text-lg">{t('common.error', 'Erro')}</strong>
        <small className="text-[80%] font-normal">{error || t('views.consumption.error_loading', 'Não foi possível carregar os dados de consumo.')}</small>
        <hr className="my-3 border-white/30" />
        <button 
          className="
            py-2 px-4
            bg-transparent hover:bg-white/10
            border border-white rounded
            text-white text-sm
            transition-colors
          " 
          onClick={onRetry}
        >
          {t('common.retry', 'Tentar novamente')}
        </button>
      </div>
    </div>
  );
}

interface EnergyHomePageContentProps {
  usageToday: number;
  currentMonthUsageAvg: number;
  currentUsage: number;
  monthlyConsumptionAvg: number;
  forecast: number;
  nextStatement: string;
  monthlyLabels: string[];
  monthlyConsumption: number[];
  goalStatus: 'Abaixo' | 'Dentro' | 'Ultrapassou' | 'Sem dados';
  hasGoal: boolean;
}

function EnergyHomePageContent({
  usageToday,
  currentMonthUsageAvg,
  currentUsage,
  monthlyConsumptionAvg,
  forecast,
  nextStatement,
  monthlyLabels,
  monthlyConsumption,
  goalStatus,
  hasGoal,
}: EnergyHomePageContentProps) {
  const { t } = useTranslation();
  const chartRef = useRef<HTMLCanvasElement>(null);

  // Delay de 500ms para animar dados nos gráficos
  const [showData, setShowData] = useState(false);
  // Estado para controlar visibilidade do alerta de meta
  const [showGoalAlert, setShowGoalAlert] = useState(true);
  
  useEffect(() => {
    const timer = setTimeout(() => setShowData(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // Cálculo dos strokes dos donuts - usa 0 enquanto não mostrar dados
  // Para energia: usamos kWh
  const donutStroke1 = showData && currentMonthUsageAvg > 0 
    ? (usageToday * 100 / currentMonthUsageAvg) 
    : 0;

  const donutStroke2 = showData && Math.floor(monthlyConsumptionAvg) > 0 
    ? (currentUsage * 100 / Math.floor(monthlyConsumptionAvg)) 
    : 0;

  const donutStroke3 = showData && Math.floor(forecast) > 0 
    ? (currentUsage * 100 / Math.floor(forecast)) 
    : 0;

  // Dias para fechamento
  const daysToClose = Math.floor(
    (new Date(nextStatement).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );

  // Mensagem baseada em meta ou período
  const message = hasGoal 
    ? t('views.layouts.home.message_goal', 'da meta definida para este período!')
    : t('views.layouts.home.message_period', 'da média de consumo dos últimos 6 meses!');

  // Captions para o gráfico
  const chartCaptions = useMemo(() => ({
    type: t('views.general.charts.type_energy', 'Energia'),
    consumption_in: t('views.general.charts.consumption_in', 'Consumo em'),
    period: t('views.general.charts.checked_months', 'Meses Aferidos'),
  }), [t]);

  // Carregar gráfico Chart.js
  useEffect(() => {
    // Só cria o gráfico após o delay de 500ms
    if (!showData) return;
    
    let chartInstance: any = null;
    
    if (chartRef.current && typeof window !== 'undefined' && (window as any).Chart) {
      const ctx = chartRef.current.getContext('2d');
      if (ctx) {
        const existingChart = (window as any).Chart.getChart?.(chartRef.current);
        if (existingChart) {
          existingChart.destroy();
        }
        
        // Dados do gráfico - cor verde para energia: #58ba18
        // Usa .slice().reverse() para inverter a ordem (mês mais antigo primeiro)
        const monthlyData = {
          labels: monthlyLabels.slice().reverse(),
          datasets: [{
            label: chartCaptions.type,
            borderColor: '#58ba18',
            borderWidth: 5,
            backgroundColor: 'rgba(224, 227, 229, 0.5)',
            pointStyle: 'circle',
            pointRadius: 7,
            pointBorderColor: '#58ba18',
            pointBackgroundColor: 'rgba(255, 255, 255, 1)',
            data: monthlyConsumption.slice().reverse(),
          }]
        };

        chartInstance = new (window as any).Chart(ctx, {
          type: 'line',
          data: monthlyData,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
              padding: {
                top: 20,
              }
            },
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                backgroundColor: 'rgba(0,0,0,0.8)',
                titleFont: {
                  size: 14,
                  weight: 'bold'
                },
                bodyFont: {
                  size: 13
                },
                callbacks: {
                  label: function(context: any) {
                    return `${chartCaptions.consumption_in} kWh: ${context.parsed.y}`;
                  }
                }
              }
            },
            scales: {
              x: {
                grid: {
                  display: false
                },
                title: {
                  display: true,
                  text: chartCaptions.period,
                  font: {
                    size: 14
                  }
                }
              },
              y: {
                beginAtZero: true,
                grid: {
                  color: 'rgba(0,0,0,0.05)'
                },
                title: {
                  display: true,
                  text: 'kWh',
                  font: {
                    size: 14
                  }
                }
              }
            }
          }
        });
      }
    }

    return () => {
      if (chartInstance) {
        chartInstance.destroy();
      }
    };
  }, [showData, monthlyLabels, monthlyConsumption, chartCaptions]);

  // Efeito de highlight na mensagem de status (igual ao original)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.href.indexOf('msg_status_goal_id') !== -1) {
      const element = document.getElementById('msg_status_goal_id');
      if (element) {
        // Scroll para centralizar o elemento na tela
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        if ((window as any).$ && (window as any).$.fn && (window as any).$.fn.effect) {
          (window as any).$(element).effect('highlight', {}, 2000);
        } else {
          // Fallback: usa CSS animation se jQuery UI não estiver disponível
          element.style.transition = 'background-color 0.5s ease';
          element.style.backgroundColor = '#ffffcc';
          setTimeout(() => {
            element.style.backgroundColor = '';
          }, 2000);
        }
      }
    }
  }, []);

  return (
    <>
      {/* Card 1: Consumo Hoje */}
      <div className="w-1/2 lg:w-1/4 px-2">
        {/* Título fora do card no mobile (igual ao original) */}
        <h4 className="hidden max-[812px]:block text-center text-[#7b8aa4] text-base font-medium mb-2">{t('views.layouts.home.today_consumption', 'Seu consumo hoje')}</h4>
        <div className="bg-white rounded-xl max-[812px]:rounded-[30px] shadow-[0_0_0_1px_rgba(16,42,67,0.04),0_1px_2px_-1px_rgba(16,42,67,0.08),0_4px_12px_-6px_rgba(16,42,67,0.10)] h-full max-[812px]:h-auto flex flex-col items-center px-4 pt-5 pb-7 max-[812px]:justify-center max-[812px]:px-3 max-[812px]:py-3">
          <h4 className="max-[812px]:hidden text-center text-[#7b8aa4] text-base font-medium leading-none mb-6">{t('views.layouts.home.today_consumption', 'Seu consumo hoje')}</h4>
          <DonutChart
            id="donut1"
            wrapperId="donut-chart"
            wrapperStyle={{ display: 'flex' }}
            value={usageToday}
            meterType="kWh"
            decimalPlaces={0}
            strokePercent={donutStroke1}
            strokeColor="#58ba18"
            shadowColor="#add917"
          />
          <div className="max-[812px]:hidden w-full rounded-full bg-[#eef2f5] text-[#7b8aa4] text-xs font-medium text-center py-2 px-4 mt-8 relative -mb-7 translate-y-1/2">
            {t('views.layouts.home.daily_average', 'Média de consumo diário')}: <span className="tabular-nums">{Math.round(currentMonthUsageAvg)}</span>&nbsp;kWh
          </div>
        </div>
        {/* Info fora do card no mobile (igual ao original) */}
        <div className="hidden max-[812px]:block text-center text-[#7b8aa4] text-[13px] font-medium mt-3 mb-2">
          {t('views.layouts.home.daily_average', 'Média de consumo diário')}: <span className="tabular-nums">{Math.round(currentMonthUsageAvg)}</span>&nbsp;kWh
        </div>
      </div>

      {/* Card 2: Consumo Até Agora */}
      <div className="w-1/2 lg:w-1/4 px-2">
        {/* Título fora do card no mobile (igual ao original) */}
        <h4 className="hidden max-[812px]:block text-center text-[#7b8aa4] text-base font-medium mb-2">{t('views.layouts.home.until_now', 'Até o momento')}</h4>
        <div className="bg-white rounded-xl max-[812px]:rounded-[30px] shadow-[0_0_0_1px_rgba(16,42,67,0.04),0_1px_2px_-1px_rgba(16,42,67,0.08),0_4px_12px_-6px_rgba(16,42,67,0.10)] h-full max-[812px]:h-auto flex flex-col items-center px-4 pt-5 pb-7 max-[812px]:justify-center max-[812px]:px-3 max-[812px]:py-3">
          <h4 className="max-[812px]:hidden text-center text-[#7b8aa4] text-base font-medium leading-none mb-6">{t('views.layouts.home.until_now_consumption', 'Seu consumo até o momento')}</h4>
          <DonutChart
            id="donut2"
            wrapperId="donut-chart2"
            wrapperStyle={{ display: 'flex' }}
            value={currentUsage}
            meterType="kWh"
            decimalPlaces={0}
            strokePercent={donutStroke2}
            strokeColor="#45a049"
            shadowColor="#57ba17"
          />
          <div className="max-[812px]:hidden w-full rounded-full bg-[#eef2f5] text-[#7b8aa4] text-xs font-medium text-center py-2 px-4 mt-8 relative -mb-7 translate-y-1/2">
            {t('views.layouts.home.monthly_average', 'Média de consumo mensal')}: <span className="tabular-nums">{Math.floor(forecast)}</span> kWh
          </div>
        </div>
        {/* Info fora do card no mobile (igual ao original) */}
        <div className="hidden max-[812px]:block text-center text-[#7b8aa4] text-[13px] font-medium mt-3 mb-2">
          {t('views.layouts.home.monthly_average', 'Média de consumo mensal')}: <span className="tabular-nums">{Math.floor(forecast)}</span> kWh
        </div>
      </div>

      {/* Divider mobile */}
      <div className="hidden w-full">
        <hr className="my-4" />
      </div>

      {/* Line Chart - Previsão de Fechamento */}
      <div className="w-full md:w-1/2 mt-3 px-2">
        <h4 className="text-[#455a64] text-base font-medium">{t('views.layouts.home.consumption_preview', 'Previsão de consumo para esse período')}</h4>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <DonutChart
            id="donut3"
            containerClassName="ml-0"
            wrapperId="donut-chart3"
            value={Math.floor(forecast)}
            meterType="kWh"
            decimalPlaces={0}
            strokePercent={donutStroke3}
            strokeColor="#2dcc6f"
            backgroundColor="#e0e3e5"
          />
          <div className="text-[17px] max-[812px]:text-[14px]">
            {daysToClose > 1 && (
              <><strong className="text-[18px] font-medium">Faltam {daysToClose} dias</strong> para o<br/>fechamento do período.</>
            )}
            {daysToClose === 1 && (
              <><strong className="text-[18px] font-medium">Falta 1 dia</strong> para o<br/>fechamento do período.</>
            )}
            {daysToClose <= 0 && (
              <><strong className="text-[18px] font-medium">Hoje</strong> está ocorrendo o<br/>fechamento do período.</>
            )}
          </div>
        </div>
      </div>

      {/* Line Chart - Consumo Mensal */}
      <div className="w-full md:w-1/2 px-2">
        <hr className="mt-8 mb-8" />
        <div className="mb-8">
          <h4 className="text-[#455a64] text-base font-medium">{t('views.layouts.home.monthly_consumption', 'Consumo mensal')}</h4>
          <p className="text-[#99abb4] text-sm">{t('views.layouts.home.monthly_consumption_subtitle', 'Acompanhe seu consumo dos períodos anteriores abaixo.')}</p>
        </div>
        <div className="bg-white rounded-[10px] p-4">
          <div className="relative w-full h-[180px] md:h-[220px]">
            <canvas ref={chartRef} id="lineChart_monthly_consumption" data-color="#58ba18"></canvas>
          </div>
        </div>
      </div>

      {/* Alerta de Status da Meta */}
      <div className="w-full md:w-1/2 px-2">
        <hr className="mt-8 mb-8" />
        <div className="mb-8">
          <h4 className="text-[#455a64] text-base font-medium">{t('views.layouts.home.messages_title', 'Informações importantes')}</h4>
          <p className="text-[#99abb4] text-sm">{t('views.layouts.home.messages_info', 'Veja algumas mensagens importantes para você.')}</p>
        </div>

        {showGoalAlert && goalStatus === 'Abaixo' && (
          <div 
            id="msg_status_goal_id" 
            className="
              relative
              pl-[60px] pr-12 py-[12px]
              !bg-[#2dcc6f] text-white
              rounded
              overflow-hidden
              after:content-[''] after:absolute after:w-[57px] after:h-[52px]
              after:bg-[url('/images/icons-set.png')] after:bg-no-repeat
              after:top-[14px] after:left-[5px]
              after:bg-[position:-58px_-166px]
            " 
            role="alert"
          >
            <button 
              type="button" 
              className="absolute top-[11px] right-[15px] p-0 bg-transparent border-0 text-[21px] font-bold leading-none cursor-pointer"
              onClick={() => setShowGoalAlert(false)}
              aria-label="Fechar"
            >
              <span className="inline-block border-2 border-white rounded-full px-[10px] py-0 text-white opacity-80 hover:opacity-100">×</span>
            </button>
            <strong className="block font-bold">{t('views.layouts.home.congratulations', 'Parabéns!')}</strong>
            <small className="text-[80%] font-normal">{t('views.layouts.home.low_consumption', 'Seu consumo está abaixo')} {message}</small>
          </div>
        )}

        {showGoalAlert && goalStatus === 'Dentro' && (
          <div 
            id="msg_status_goal_id" 
            className="
              relative
              pl-[60px] pr-12 py-[12px]
              !bg-[#f1c40e] text-white
              rounded
              overflow-hidden
              after:content-[''] after:absolute after:w-[57px] after:h-[52px]
              after:bg-[url('/images/icons-set.png')] after:bg-no-repeat
              after:top-[14px] after:left-[5px]
              after:bg-[position:0_-169px]
            " 
            role="alert"
          >
            <button 
              type="button" 
              className="absolute top-[11px] right-[15px] p-0 bg-transparent border-0 text-[21px] font-bold leading-none cursor-pointer"
              onClick={() => setShowGoalAlert(false)}
              aria-label="Fechar"
            >
              <span className="inline-block border-2 border-white rounded-full px-[10px] py-0 text-white opacity-80 hover:opacity-100">×</span>
            </button>
            <strong className="block font-bold">{t('views.layouts.home.attention', 'Atenção!')}</strong>
            <small className="text-[80%] font-normal">{t('views.layouts.home.mid_consumption', 'Seu consumo está próximo')} {message}</small>
          </div>
        )}

        {showGoalAlert && (goalStatus === 'Ultrapassou' || goalStatus === 'Sem dados') && (
          <div 
            id="msg_status_goal_id" 
            className="
              relative
              pl-[60px] pr-12 py-[12px]
              !bg-[#e84b3d] text-white
              rounded
              overflow-hidden
              after:content-[''] after:absolute after:w-[57px] after:h-[52px]
              after:bg-[url('/images/icons-set.png')] after:bg-no-repeat
              after:top-[14px] after:left-[5px]
              after:bg-[position:11px_-226px]
            " 
            role="alert"
          >
            <button 
              type="button" 
              className="absolute top-[11px] right-[15px] p-0 bg-transparent border-0 text-[21px] font-bold leading-none cursor-pointer"
              onClick={() => setShowGoalAlert(false)}
              aria-label="Fechar"
            >
              <span className="inline-block border-2 border-white rounded-full px-[10px] py-0 text-white opacity-80 hover:opacity-100">×</span>
            </button>
            <strong className="block font-bold">{t('views.layouts.home.warning', 'Cuidado!')}</strong>
            <small className="text-[80%] font-normal">{t('views.layouts.home.high_consumption', 'Seu consumo está acima')} {message}</small>
          </div>
        )}
      </div>
    </>
  );
}
